import React, { useMemo, useState } from 'react'
import { CoinImage, CoinListRow, CoinText, Input, InputLabel } from './ListView.styled'
import { Balance } from './ListView.balance'
import { getLogoForCoin } from '@src/service/coins/coins'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { toast } from 'react-toastify'
import { t } from 'i18next'
import { getWallet } from '@src/reducers/wallet.helper'
import { CurrencyEnum } from '@src/service/coins/coins.types'

interface CurrenciestListProps {
  currencies: string[]
  searchTerm?: string
  activeWallet?: { currency: string }
  isGamblerWalletActive?: boolean
  noBalance?: boolean
  setActive: (e: React.MouseEvent<HTMLDivElement>, currency: keyof CurrencyEnum) => void
}

export const CurrenciestList: React.FC<CurrenciestListProps> = ({
  currencies,
  searchTerm = '',
  activeWallet,
  isGamblerWalletActive = false,
  noBalance,
  setActive,
}) => {
  const [showOnlyWithBalance, setShowOnlyWithBalance] = useState(() => {
    return JSON.parse(localStorage.getItem('showOnlyWithBalance') || 'false')
  })

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    setShowOnlyWithBalance(isChecked)
    localStorage.setItem('showOnlyWithBalance', JSON.stringify(isChecked))

    if (isChecked) {
      toast.success(t('wallet.listView.notification', "Your zero balances won't appear in your wallet"))
    }
  }

  const filterAndSortCurrencies = (currencies, searchTerm, showOnlyWithBalance, getWallet) => {
    return currencies.reduce((acc, currency) => {
      const matchesSearchTerm = currency.includes(searchTerm.toLocaleUpperCase())
      const hasBalance = !showOnlyWithBalance || (getWallet(currency)?.balance ?? 0) > 0
      if (matchesSearchTerm && hasBalance) {
        acc.push(currency)
      }
      return acc
    }, [])
  }

  const filteredAndSortedCurrencies = useMemo(() => {
    return filterAndSortCurrencies(currencies, searchTerm, showOnlyWithBalance, getWallet)
  }, [currencies, searchTerm, showOnlyWithBalance, getWallet])

  return (
    <div>
      {!noBalance && (
        <InputLabel>
          <Input type="checkbox" checked={showOnlyWithBalance} onChange={handleCheckboxChange} className="mr-5" />
          {t('wallet.listView.checkboxLabel', 'Hide zero balances')}
        </InputLabel>
      )}
      <TransitionGroup>
        {filteredAndSortedCurrencies.map((currency) => (
          <CSSTransition key={'wallet-list-x' + currency} timeout={200} classNames="fade">
            <CoinListRow
              $isActive={activeWallet?.currency === currency && !isGamblerWalletActive}
              onClick={(e) => setActive(e, currency)}
            >
              <div className="flex">
                <CoinImage alt={currency} src={getLogoForCoin(currency)} />
                <CoinText className="coin">{currency}</CoinText>
              </div>
              {!noBalance && <Balance currency={currency} />}
            </CoinListRow>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  )
}

export default CurrenciestList
